// Profile-menu identity source. Combines the backend `GET /api/users/me`
// (authoritative email/timezone; §users schema has no name/picture) with the
// Auth0 SDK's decoded profile claims (name/picture only -- never `sub`,
// which is an internal identifier and must not render per
// docs/agent/rules/data-and-db.md security section).
//
// In mock-auth mode (dev/e2e -- Auth0ProviderWithNavigate never mounts an
// Auth0Provider) `useAuth0()` still returns its default context value with
// `user: undefined`, so this degrades to the backend email/`Account` label
// rather than throwing.
import { useAuth0 } from '@auth0/auth0-react';
import { useMe } from './useMe';

export interface CurrentUser {
  /** Display name -- Auth0 profile name, else email, else a generic label. Never the Auth0 `sub`. */
  name: string;
  email: string | null;
  /** Auth0 profile picture URL, if present. */
  picture: string | null;
  /** Two-letter fallback avatar glyph derived from name/email. */
  initials: string;
}

function initialsFrom(name: string, email: string | null): string {
  const source = name.trim() || (email ?? '').trim();
  if (!source) return '?';
  const words = source.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0]![0]}${words[words.length - 1]![0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

/**
 * Returns `null` while neither the Auth0 profile nor the backend record has
 * resolved yet -- callers should render a loading affordance, not fabricate
 * a placeholder identity.
 */
export function useCurrentUser(): CurrentUser | null {
  const { user: auth0User } = useAuth0();
  const me = useMe();

  const auth0Name = typeof auth0User?.name === 'string' ? auth0User.name : null;
  const auth0Email = typeof auth0User?.email === 'string' ? auth0User.email : null;
  const auth0Picture = typeof auth0User?.picture === 'string' ? auth0User.picture : null;

  const email = me.data?.email ?? auth0Email ?? null;
  if (!auth0User && !me.data) return null;

  const name = auth0Name ?? email ?? 'Account';
  return {
    name,
    email,
    picture: auth0Picture,
    initials: initialsFrom(name, email),
  };
}
